/**
 * Reviewing mytender.io's revisions from the pane. Needs WordApi 1.6
 * (tracked change objects); without it the strip only reports what was
 * written and the user reviews in Word's own Review tab.
 *
 * Word's unit is the revision: one per run of inserted text, one per
 * paragraph mark, one per table row, so a replaced answer is dozens. The
 * pane's unit is the suggestion: every revision inside one question's answer
 * block is one suggestion (named after the question), and outside blocks a
 * run of consecutive changed paragraphs is one. Word's own Review pane still
 * shows the revisions; the pane counts, selects and resolves suggestions.
 *
 * "mytender.io's revisions" are the ones authored "mytender.io" when edits
 * went in as authored OOXML revisions; when they went in as ordinary tracked
 * changes they carry the user's name and cannot be told apart from theirs,
 * so the scope is then every tracked change in the document.
 */
import { REVISION_AUTHOR } from "./authoredRevision";
import { documentCapabilities } from "./snapshot";

export type ReviewAction = "count" | "accept_all" | "reject_all";

export type ReviewScope = {
  /** Only revisions by this author; null for every tracked change. */
  author: string | null;
};

export type ReviewResult = {
  supported: boolean;
  /** Revisions in scope before the action. */
  count: number;
  accepted?: number;
  rejected?: number;
  scope: "mytender" | "all";
};

export const scopeFor = (authored: boolean): ReviewScope => ({ author: authored ? REVISION_AUTHOR : null });

const inScope = (changes: Word.TrackedChange[], scope: ReviewScope): Word.TrackedChange[] =>
  scope.author ? changes.filter((c) => c.author === scope.author) : changes;

/**
 * Count, accept or reject every tracked change in scope.
 * @param action - What to do.
 * @param scope - Whose changes.
 * @returns The outcome.
 */
export const reviewChanges = async (action: ReviewAction, scope: ReviewScope): Promise<ReviewResult> => {
  const scopeName = scope.author ? "mytender" : "all";
  if (!documentCapabilities().review) return { supported: false, count: 0, scope: scopeName };
  return Word.run(async (context) => {
    const changes = context.document.body.getTrackedChanges();
    changes.load("items/author,items/type");
    await context.sync();
    const targets = inScope(changes.items, scope);
    const count = targets.length;
    if (action === "count" || count === 0) return { supported: true, count, scope: scopeName };
    await unbox(context, null);
    for (const change of targets) {
      if (action === "accept_all") change.accept();
      else change.reject();
    }
    await context.sync();
    return {
      supported: true,
      count,
      scope: scopeName,
      ...(action === "accept_all" ? { accepted: count } : { rejected: count }),
    };
  });
};

// --------------------------------------------------------------------------- //
// Suggestions
// --------------------------------------------------------------------------- //
export type SuggestionKind = "inserted" | "deleted" | "changed";

export type Suggestion = {
  /** Stable within one listing: the block's tag, or the first revision's position. */
  key: string;
  /** The question ("2.1 · Social value") or the first words of the passage. */
  title: string;
  /** Whether it is a question's answer (named after the question). */
  inBlock: boolean;
  kind: SuggestionKind;
  /** Word revisions it is made of. */
  revisions: number;
};

export type SelectedSuggestion = Suggestion & { index: number; text: string };

const QUESTION_TAG = /^mytender-q:/;

type Group = {
  key: string;
  title: string;
  inBlock: boolean;
  block: Word.ContentControl | null;
  changes: Word.TrackedChange[];
  first: Word.Range;
  last: Word.Range;
};

/**
 * Every in-scope revision, grouped into suggestions in document order. One
 * round trip for the revisions, one for what each sits in.
 */
const collect = async (context: Word.RequestContext, scope: ReviewScope): Promise<Group[]> => {
  const changes = context.document.body.getTrackedChanges();
  changes.load("items/author,items/type");
  await context.sync();
  const targets = inScope(changes.items, scope);
  if (!targets.length) return [];

  const meta = targets.map((c) => {
    const range = c.getRange();
    // Navigation properties are proxies resolved at sync; the rules can't see that.
    // eslint-disable-next-line office-addins/load-object-before-read, office-addins/call-sync-before-read
    const block = range.parentContentControlOrNullObject;
    block.load("isNullObject,tag,title");
    // eslint-disable-next-line office-addins/load-object-before-read
    const paragraph = range.paragraphs.getFirst();
    paragraph.load("text");
    return { change: c, range, block, paragraph };
  });
  // Whether each revision's paragraph is the same as, or right before, the
  // next revision's: consecutive changed paragraphs form one suggestion.
  const adjacency = meta
    .slice(0, -1)
    .map((m, i) => m.paragraph.getRange("Whole").compareLocationWith(meta[i + 1].paragraph.getRange("Whole")));
  await context.sync();

  const blockTagOf = (i: number): string | null => {
    const b = meta[i].block;
    return !b.isNullObject && QUESTION_TAG.test(b.tag ?? "") ? b.tag : null;
  };
  const adjacent = (i: number): boolean => {
    const v = adjacency[i]?.value;
    return v === "Equal" || v === "AdjacentBefore" || v === "Contains" || v === "Inside";
  };
  const groups: Group[] = [];
  for (let i = 0; i < meta.length; i += 1) {
    const m = meta[i];
    const tag = blockTagOf(i);
    const current = groups[groups.length - 1];
    if (tag && current?.key === tag) {
      current.changes.push(m.change);
      current.last = m.range;
      continue;
    }
    if (!tag && current && !current.inBlock && adjacent(i - 1)) {
      current.changes.push(m.change);
      current.last = m.range;
      continue;
    }
    if (tag && current && !current.inBlock && adjacent(i - 1)) {
      // A question's heading, written just above its block, belongs with it.
      current.key = tag;
      current.title = m.block.title || current.title;
      current.inBlock = true;
      current.block = m.block;
      current.changes.push(m.change);
      current.last = m.range;
      continue;
    }
    groups.push({
      key: tag ?? `run:${i}`,
      title: tag ? m.block.title || "Answer" : (m.paragraph.text ?? "").replace(/\s+/g, " ").trim().slice(0, 80),
      inBlock: Boolean(tag),
      block: tag ? m.block : null,
      changes: [m.change],
      first: m.range,
      last: m.range,
    });
  }
  return groups;
};

const kindOf = (changes: Word.TrackedChange[]): SuggestionKind => {
  const added = changes.some((c) => c.type === "Added");
  const deleted = changes.some((c) => c.type === "Deleted");
  return added && deleted ? "changed" : deleted ? "deleted" : "inserted";
};

const toSuggestion = (g: Group): Suggestion => ({
  key: g.key,
  title: g.title,
  inBlock: g.inBlock,
  kind: kindOf(g.changes),
  revisions: g.changes.length,
});

/**
 * The last listing, reused while the number of in-scope revisions is
 * unchanged: grouping reads several objects per revision, which a document
 * with hundreds of them (every answer replaced) can't afford every few
 * seconds, while the count is one cheap call.
 */
let cached: { author: string | null; revisions: number; list: Suggestion[] } | null = null;

/** The answer block Review last boxed (its tag), so the next step can unbox it. */
let boxed: string | null = null;

/** Hide the box drawn on the last reviewed answer, unless `keep` is that block. */
const unbox = async (context: Word.RequestContext, keep: Word.ContentControl | null): Promise<void> => {
  if (!boxed) return;
  const tag = boxed;
  boxed = null;
  const control = context.document.body.contentControls.getByTag(tag).getFirstOrNullObject();
  control.load("isNullObject");
  if (keep) keep.load("tag");
  await context.sync();
  if (control.isNullObject || (keep && keep.tag === tag)) {
    if (keep && keep.tag === tag) boxed = tag;
    return;
  }
  control.appearance = "Hidden";
  await context.sync();
};

/** Hide the box left on the last reviewed answer (the bar emptied or closed). */
export const clearReviewBox = async (): Promise<void> => {
  if (!boxed || !documentCapabilities().review) return;
  await Word.run((context) => unbox(context, null)).catch(() => undefined);
};

/** The suggestions in the document, in order. */
export const listSuggestions = async (scope: ReviewScope): Promise<Suggestion[]> => {
  if (!documentCapabilities().review) return [];
  return Word.run(async (context) => {
    const changes = context.document.body.getTrackedChanges();
    changes.load("items/author");
    await context.sync();
    const revisions = inScope(changes.items, scope).length;
    if (cached && cached.author === scope.author && cached.revisions === revisions) return cached.list;
    const list = revisions ? (await collect(context, scope)).map(toSuggestion) : [];
    cached = { author: scope.author, revisions, list };
    return list;
  });
};

/** Which suggestion to select: the one with `key`, the one after `afterKey`, or the one at `index`. */
export type SuggestionTarget = { key?: string; afterKey?: string; index?: number };

/**
 * Select a suggestion in Word so the user can read it in place: the whole
 * answer for a question, the changed passage otherwise. Suggestions are
 * found by key (a question's tag is stable; a passage's key is its position)
 * because the list can change under the pane; the first is the fallback.
 * @param scope - Whose changes.
 * @param target - Which suggestion.
 * @returns What was selected, or null when there is nothing to review.
 */
export const selectSuggestion = async (
  scope: ReviewScope,
  target: SuggestionTarget = {}
): Promise<SelectedSuggestion | null> => {
  if (!documentCapabilities().review) return null;
  return Word.run(async (context) => {
    const groups = await collect(context, scope);
    if (!groups.length) return null;
    const byKey = target.key !== undefined ? groups.findIndex((g) => g.key === target.key) : -1;
    const afterKey = target.afterKey !== undefined ? groups.findIndex((g) => g.key === target.afterKey) : -1;
    const wanted = byKey >= 0 ? byKey : afterKey >= 0 ? afterKey + 1 : (target.index ?? 0);
    const at = wanted >= 0 && wanted < groups.length ? wanted : 0;
    const g = groups[at];
    await unbox(context, g.block);
    const range = g.block ? g.block.getRange("Whole") : g.first.expandTo(g.last);
    if (g.block) {
      // An answer is shown boxed with the cursor at its start (a selection
      // could be typed over); a passage outside any block is selected.
      g.block.appearance = "BoundingBox";
      boxed = g.key;
      g.block.getRange("Start").select();
    } else range.select(Word.SelectionMode.select);
    const kind = kindOf(g.changes);
    const text = range.getReviewedText(kind === "deleted" ? "Original" : "Current");
    await context.sync();
    return { ...toSuggestion(g), index: at, text: (text.value ?? "").replace(/\s+/g, " ").trim() };
  });
};

/**
 * Accept or reject one suggestion: every revision it is made of.
 * @param scope - Whose changes.
 * @param key - Which suggestion (see {@link Suggestion.key}); nothing happens
 *   when it is no longer in the document.
 * @param action - What to do.
 * @returns How many revisions were resolved.
 */
export const resolveSuggestion = async (
  scope: ReviewScope,
  key: string,
  action: "accept" | "reject"
): Promise<number> => {
  if (!documentCapabilities().review) return 0;
  return Word.run(async (context) => {
    const groups = await collect(context, scope);
    const g = groups.find((x) => x.key === key);
    if (!g) return 0;
    await unbox(context, null);
    for (const change of g.changes) {
      if (action === "accept") change.accept();
      else change.reject();
    }
    await context.sync();
    return g.changes.length;
  });
};
